import moment from "moment";

/**
 * SharePoint Service to fetch calendar events from SharePoint List
 */

// Parse SharePoint list URL to extract site and list information
const parseSharePointUrl = (url) => {
  try {
    // Extract the list name from the URL
    // Format: https://myoffice.accenture.com/:l:/r/personal/[user]/Lists/[ListName]
    const match = url.match(/Lists\/([^?]+)/);
    const listName = match ? match[1] : null;
    return { listName };
  } catch (error) {
    console.error("Error parsing SharePoint URL:", error);
    return null;
  }
};

/**
 * Fetch events from SharePoint List via proxy server
 * @param {string} sharePointUrl - The SharePoint list URL
 * @returns {Promise<Array>} - Array of calendar events
 */
export const fetchSharePointEvents = async (sharePointUrl) => {
  try {
    const urlInfo = parseSharePointUrl(sharePointUrl);
    
    if (!urlInfo || !urlInfo.listName) {
      throw new Error("Invalid SharePoint URL");
    }

    // Extract the personal site path from the URL
    const personalSiteMatch = sharePointUrl.match(/personal\/([^\/]+)/);
    const personalSite = personalSiteMatch ? personalSiteMatch[1] : null;

    if (!personalSite) {
      throw new Error("Could not extract personal site from URL");
    }

    // Construct the SharePoint REST API endpoint
    const baseUrl = "https://myoffice.accenture.com";
    const apiUrl = `${baseUrl}/personal/${personalSite}/_api/web/lists/getbytitle('${urlInfo.listName}')/items`;

    console.log("Fetching from SharePoint via proxy:", apiUrl);

    // Use proxy server to avoid CORS issues
    const proxyUrl = `http://localhost:3002/api/sharepoint?listUrl=${encodeURIComponent(apiUrl)}`;
    
    const response = await fetch(proxyUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`SharePoint API error: ${response.status} - ${errorData.message || response.statusText}`);
    }

    const data = await response.json();
    
    console.log("SharePoint response:", data);
    
    // Parse SharePoint list items to calendar events format
    // Handle different possible field names from SharePoint
    const events = data.d.results.map((item) => {
      // Try different field name variations
      const startDate = item.StartDate || item.Start_x0020_Date || item.EventDate || item.Start;
      const endDate = item.EndDate || item.End_x0020_Date || item.EventDate || item.End;
      const category = item.Category || item.Event_x0020_Category || "program";
      const type = item.Type || item.Event_x0020_Type || "program";
      
      return {
        id: item.Id || item.ID,
        title: item.Title,
        start: moment(startDate),
        end: moment(endDate),
        category: category.toLowerCase(),
        type: type.toLowerCase(),
        description: item.Description || "",
      };
    });

    console.log("Parsed events:", events);
    return events;
  } catch (error) {
    console.error("Error fetching SharePoint events:", error);
    throw error;
  }
};

/**
 * Fetch events using Microsoft Graph API (alternative approach)
 * This requires the SharePoint site to be accessible via Graph API
 */
export const fetchSharePointEventsViaGraph = async (siteId, listId, accessToken) => {
  try {
    const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items?expand=fields`;

    const response = await fetch(graphUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status}`);
    }

    const data = await response.json();
    
    // Parse Graph API response to calendar events
    const events = data.value.map((item) => ({
      id: item.id,
      title: item.fields.Title,
      start: moment(item.fields.StartDate || item.fields.EventDate),
      end: moment(item.fields.EndDate || item.fields.EventDate),
      category: item.fields.Category || "program",
      type: item.fields.Type || "program",
      description: item.fields.Description || "",
    }));

    return events;
  } catch (error) {
    console.error("Error fetching events via Graph API:", error);
    throw error;
  }
};

/**
 * Mock function for development/testing without authentication
 * Returns sample events in the same format as SharePoint would return
 */
export const fetchMockSharePointEvents = async () => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  return [
    {
      id: 1,
      title: "Quantum Computing Fundamentals",
      start: moment("2026-02-10 09:00"),
      end: moment("2026-02-10 11:00"),
      category: "program",
      type: "program",
      description: "Introduction to quantum computing principles",
    },
    {
      id: 2,
      title: "Algorithm Design Workshop",
      start: moment("2026-02-11 14:00"),
      end: moment("2026-02-11 16:00"),
      category: "program",
      type: "program",
      description: "Advanced algorithm design techniques",
    },
    {
      id: 3,
      title: "Technical Assessment - Module 1",
      start: moment("2026-02-12 13:00"),
      end: moment("2026-02-12 15:00"),
      category: "exam",
      type: "assessment",
      description: "First module technical evaluation",
    },
  ];
};

export default {
  fetchSharePointEvents,
  fetchSharePointEventsViaGraph,
  fetchMockSharePointEvents,
};
